/**
 * Generic Carousel Ad Mockup Component
 *
 * Reusable carousel component for platforms that support carousel ads:
 * - Facebook/Meta: Carousel ads with 5 cards max
 * - Instagram: Carousel posts with swipe navigation
 * - Pinterest: Idea Pins with vertical scrolling
 *
 * Features:
 * - Multiple cards with images/videos
 * - Navigation controls (prev/next buttons)
 * - Indicator dots showing current position
 * - Responsive design
 * - Platform-specific styling via className
 */

import React, { useState } from 'react';
import { stripMediaTags } from '@/utils/mediaTagParser';
import { AdMockupContainer } from '../base/AdMockupContainer';
import { AdMockupMedia } from '../base/AdMockupMedia';
import { AdMockupCTA } from '../base/AdMockupCTA';

export interface CarouselCard {
  id: string;
  image?: string;
  video?: string;
  headline?: string;
  description?: string;
  callToAction?: string;
  linkUrl?: string;
}

export interface CarouselAdMockupProps {
  cards: CarouselCard[];
  platform: 'facebook' | 'instagram' | 'pinterest' | 'meta';
  brandName?: string;
  brandLogoUrl?: string;
  maxCards?: number; // Platform-specific limit
  cardAspectRatio?: '1:1' | '16:9' | '4:5';
  showIndicators?: boolean;
  autoPlay?: boolean;
  autoPlayInterval?: number;
}

export const CarouselAdMockup: React.FC<CarouselAdMockupProps> = ({
  cards,
  platform,
  brandName = 'Your Brand',
  brandLogoUrl,
  maxCards = 5,
  cardAspectRatio = '1:1',
  showIndicators = true,
  autoPlay = false,
  autoPlayInterval = 5000,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const displayCards = cards.slice(0, maxCards);

  // Auto-play functionality
  React.useEffect(() => {
    if (!autoPlay || displayCards.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % displayCards.length);
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [autoPlay, autoPlayInterval, displayCards.length]);

  const goToPrevious = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? displayCards.length - 1 : prev - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prev) =>
      (prev + 1) % displayCards.length
    );
  };

  const goToCard = (index: number) => {
    setCurrentIndex(index);
  };

  const currentCard = displayCards[currentIndex];
  const mediaUrl = currentCard.image || currentCard.video;

  // Platform-specific styling
  const containerClasses = {
    facebook: 'max-w-xl',
    meta: 'max-w-xl',
    instagram: 'max-w-md',
    pinterest: 'max-w-sm',
  };

  const headerClasses = {
    facebook: 'px-4 py-3',
    meta: 'px-4 py-3',
    instagram: 'p-3',
    pinterest: 'p-3',
  };

  return (
    <AdMockupContainer platform={platform}>
      <div className={`bg-white dark:bg-card-darkAlt rounded-lg border border-gray-200 dark:border-border-dark overflow-hidden shadow-sm ${containerClasses[platform]} mx-auto`}>

        {/* Header - Platform specific */}
        {(platform === 'facebook' || platform === 'meta') && (
          <div className={`flex items-center gap-3 ${headerClasses[platform]} border-b border-gray-200 dark:border-border-dark`}>
            {brandLogoUrl ? (
              <img
                src={brandLogoUrl}
                alt={brandName}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold text-sm">
                {brandName.slice(0, 2).toUpperCase()}
              </div>
            )}
            <div className="flex-1">
              <p className="font-semibold text-sm text-gray-900 dark:text-white">
                {brandName}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Sponsored</p>
            </div>
            <button className="text-gray-400 hover:text-gray-600">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
              </svg>
            </button>
          </div>
        )}

        {/* Instagram Header */}
        {platform === 'instagram' && (
          <div className="flex items-center gap-3 p-3 border-b border-gray-200 dark:border-border-dark">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 p-0.5">
              <div className="w-full h-full rounded-full bg-white dark:bg-card-darkAlt flex items-center justify-center">
                {brandLogoUrl ? (
                  <img
                    src={brandLogoUrl}
                    alt={brandName}
                    className="w-7 h-7 rounded-full object-cover"
                  />
                ) : (
                  <div className="text-xs font-bold text-primary">
                    {brandName.slice(0, 1).toUpperCase()}
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm text-gray-900 dark:text-white">
                {brandName}
              </p>
              <p className="text-xs text-gray-500">Sponsored</p>
            </div>
          </div>
        )}

        {/* Carousel Container */}
        <div className="relative bg-gray-100 dark:bg-card-darkAlt">
          {/* Current Card */}
          {mediaUrl && (
            <AdMockupMedia
              mediaUrl={mediaUrl}
              aspectRatio={cardAspectRatio}
              platform={platform}
              alt={`Carousel card ${currentIndex + 1}`}
            />
          )}

          {/* Navigation Arrows - Only show if more than one card */}
          {displayCards.length > 1 && (
            <>
              {/* Previous Button */}
              <button
                onClick={goToPrevious}
                className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white/80 hover:bg-white text-gray-900 dark:bg-white/10 dark:text-white dark:hover:bg-white/10 transition-colors shadow-lg"
                aria-label="Previous card"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {/* Next Button */}
              <button
                onClick={goToNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white/80 hover:bg-white text-gray-900 dark:bg-white/10 dark:text-white dark:hover:bg-white/10 transition-colors shadow-lg"
                aria-label="Next card"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          {/* Card Counter */}
          <div className="absolute bottom-4 left-4 bg-black/60 px-3 py-1 rounded-full text-white text-xs font-medium">
            {currentIndex + 1} / {displayCards.length}
          </div>
        </div>

        {/* Card Content */}
        <div className="p-4">
          {currentCard.headline && (
            <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">
              {stripMediaTags(currentCard.headline)}
            </h3>
          )}

          {currentCard.description && (
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-2">
              {stripMediaTags(currentCard.description)}
            </p>
          )}

          {currentCard.callToAction && (
            <AdMockupCTA
              text={currentCard.callToAction}
              platform={platform}
              variant="primary"
              fullWidth={true}
            />
          )}
        </div>

        {/* Indicator Dots - Only show if enabled and more than one card */}
        {showIndicators && displayCards.length > 1 && (
          <div className="flex justify-center gap-2 pb-4">
            {displayCards.map((_, index) => (
              <button
                key={index}
                onClick={() => goToCard(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex
                    ? 'bg-primary w-6'
                    : 'bg-gray-300 dark:bg-white/15 hover:bg-gray-400'
                }`}
                aria-label={`Go to card ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </AdMockupContainer>
  );
};

export default CarouselAdMockup;
